import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import {
  Newspaper,
  Star,
  BellRing,
  Sparkles,
} from "lucide-react";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import {
  CommonInput,
  CommonSelect,
  CommonViewToggle,
} from "../../../../Components/ui";

const NEWS_SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "title-asc", label: "Title (A - Z)" },
  { value: "title-desc", label: "Title (Z - A)" },
];

const CATEGORY_TABS = [
  { id: "all", label: "All News", icon: Newspaper, color: colors.primary.main },
  { id: "featured", label: "Featured", icon: Star, color: colors.amber[600] },
  { id: "announcements", label: "Announcements", icon: BellRing, color: colors.purple[600] },
  { id: "updates", label: "Updates", icon: Sparkles, color: colors.emerald[600] },
];

const NewsFilters = ({
  activeTab = "all",
  onTabChange,
  searchQuery = "",
  onSearchChange,
  sortBy = "newest",
  onSortChange,
  viewMode = "grid",
  onViewModeChange,
  countsByTab = {},
  onRefresh,
  refreshing = false,
}) => {
  return (
    <Card
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        mb: 3.5,
      }}
    >
      <Stack spacing={2.5}>
        {/* Top: Category Tabs */}
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{
            overflowX: "auto",
            pb: { xs: 1, sm: 0 },
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          {CATEGORY_TABS.map((tab) => {
            const isSelected = String(activeTab) === String(tab.id);
            const TabIcon = tab.icon;
            const count = countsByTab[tab.id] !== undefined ? countsByTab[tab.id] : 0;

            return (
              <Box
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: borderRadiusPx.lg,
                  cursor: "pointer",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  transition: "all 0.2s ease-in-out",
                  backgroundColor: isSelected ? colors.primary.main : colors.slate[50],
                  border: `1px solid ${isSelected ? colors.primary.main : colors.slate[200]}`,
                  boxShadow: isSelected ? "0 4px 12px rgba(37, 99, 235, 0.25)" : "none",
                  "&:hover": {
                    backgroundColor: isSelected ? colors.primary.dark : colors.slate[100],
                    borderColor: isSelected ? colors.primary.dark : colors.slate[300],
                  },
                }}
              >
                <TabIcon size={16} color={isSelected ? colors.neutral.white : tab.color} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isSelected ? 700 : 600,
                    fontSize: "0.875rem",
                    color: isSelected ? colors.neutral.white : colors.slate[700],
                  }}
                >
                  {tab.label}
                </Typography>
                <Box
                  sx={{
                    px: 0.85,
                    py: 0.15,
                    borderRadius: borderRadiusPx.full,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    backgroundColor: isSelected ? "rgba(255, 255, 255, 0.25)" : colors.slate[200],
                    color: isSelected ? colors.neutral.white : colors.slate[700],
                  }}
                >
                  {count}
                </Box>
              </Box>
            );
          })}
        </Stack>

        {/* Bottom Bar: Search, Sort, View Toggle & Refresh */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          {/* Search Input */}
          <Box sx={{ width: { xs: "100%", md: "360px" } }}>
            <CommonInput
              fullWidth
              size="small"
              placeholder="Search news by title, keyword..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.slate[400], fontSize: 20 }} />
                </InputAdornment>
              }
              endAdornment={
                searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => onSearchChange("")} sx={{ p: 0.25 }}>
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            />
          </Box>

          {/* Right Controls: Sort Dropdown, View Toggle, Refresh */}
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent={{ xs: "space-between", md: "flex-end" }}
            flexWrap="wrap"
            sx={{ gap: 1 }}
          >
            {/* Sort Dropdown */}
            <Box sx={{ minWidth: "170px" }}>
              <CommonSelect
                size="small"
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value)}
                options={NEWS_SORT_OPTIONS}
              />
            </Box>

            {/* Grid / List View Switcher */}
            <CommonViewToggle
              value={viewMode}
              onChange={onViewModeChange}
              size="small"
            />

            {/* Refresh Button */}
            <Tooltip title="Refresh news feed">
              <IconButton
                onClick={onRefresh}
                disabled={refreshing}
                sx={{
                  bgcolor: colors.slate[50],
                  color: colors.slate[600],
                  border: `1px solid ${colors.slate[200]}`,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: colors.slate[100],
                    color: colors.slate[900],
                  },
                }}
              >
                <RefreshIcon
                  sx={{
                    fontSize: 18,
                    animation: refreshing ? "spin 1s linear infinite" : "none",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};

NewsFilters.propTypes = {
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  searchQuery: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  sortBy: PropTypes.string.isRequired,
  onSortChange: PropTypes.func.isRequired,
  viewMode: PropTypes.oneOf(["grid", "list"]).isRequired,
  onViewModeChange: PropTypes.func.isRequired,
  countsByTab: PropTypes.object,
  onRefresh: PropTypes.func.isRequired,
  refreshing: PropTypes.bool,
};

export default NewsFilters;
